import { QueryResponse as PrefferedSPIMethodResponse } from 'api/queries/getPrefferedSPIMethod.query';
import { QueryResponse as PreferencePartialCompletion } from 'api/queries/getSubProgramPreferencePartialCompletion.query';

import { ProgressMeasurement } from 'components/common/GlobalFiltersInPage/components/progressMeasurementFilter/progressMeasurementFilter';

export interface IStatus {
    id: string;
    name: string;
    label: string;
}

export interface IAssignee {
    id: number;
    userName: string;
    firstName: string;
    lastName: string;
    imageName: string;
}

export interface ICategory {
    id: number;
    name: string;
    label: string;
}

export interface IType {
    id: number;
    name: string;
    label: string;
}

export interface ICardIndicator {
    isIndicatorOn: boolean;
    text: string;
}

export interface ICardIndicators {
    isOverdue: ICardIndicator;
    isCriticalPath: ICardIndicator;
    isLongestPath: ICardIndicator;
    isDelayDriver: ICardIndicator;
    isConstraintDaySet: ICardIndicator;
    isImportantStalled: ICardIndicator;
    isImportantPredecessorOverdue: ICardIndicator;
    isRcfRisk: ICardIndicator;
    isLongDuration: ICardIndicator;
    isImportantLag: ICardIndicator;
    isSetBack: ICardIndicator;
    isKcRisk: ICardIndicator;
}

export interface ICardBoardViewResponse {
    id: number;
    metaDataId: number;
    criticalityScore: number;
    float: number;
    taskHashCode: string | null;
    taskVersionHashCode: string;
    plannedStartDate: number;
    plannedFinishDate: number;
    actualStartDate: number | null;
    actualFinishDate: number | null;
    numOfPredecessors: number;
    numOfSuccessors: number;
    projectWeekStart: number;
    projectWeekFinish: number;
    projectMonthStart: number;
    projectMonthFinish: number;
    status: IStatus;
    originalStatus: IStatus;
    taskActivityId: string;
    parentActivity: string;
    grandParentActivity: string;
    plannedDurationInDays: number;
    actualDurationInDays: number;
    remainingDurationInDays: number;
    completedDurationInDays: number;
    taskActivityType: string;
    isVirtual: boolean;
    category: ICategory;
    title: string;
    startDate: number;
    endDate: number;
    dueDate: number | null;
    duration: number;
    rank: number;
    tagList: string[];
    versionDate: number;
    isStartOverdue: boolean;
    isFinishOverdue: boolean;
    taskId: number;
    startVariance: number;
    finishVariance: number;
    currentDurationOverrun: number;
    completedDurationOverrun: number;
    isInterimChanged: boolean;
    severity: string | null;
    overrunPercent: number;
    assignee: IAssignee;
    details: string;
    cardLastComment: string | null;
    cardIndicators: ICardIndicators;
    submittedDurationComplete: number;
    isTracked: boolean;
    precedingActivities: number;
    succeedingActivites: number;
    precedingRisk: string;
    succeedingRisk: string;
    type: IType;
    baselineStartDate: number;
    baselineFinishDate: number;
    orderInPage: number | null;
    uniqId: string | null;
    isGenAiDetails: boolean;
    allBudgetResourceUnit: number;
    allActualResourceUnit: number;
    allResourceNameList: string[];
    comparedBudgetResourceUnit: number | null;
    actualResourceUnit: number | null;
    remainingResourceUnit: number | null;
    resourceName: string | null;
    resourceType: string | null;
}

export interface IActivity {
    id: number;
    metaDataId: number;
    criticalityScore: number;
    float: number;
    taskHashCode: string | null;
    taskVersionHashCode: string;
    plannedStartDate: number;
    plannedFinishDate: number;
    actualStartDate: number | null;
    actualFinishDate: number | null;
    numOfPredecessors: number;
    numOfSuccessors: number;
    projectWeekStart: number;
    projectWeekFinish: number;
    projectMonthStart: number;
    projectMonthFinish: number;
    status: IStatus | null;
    originalStatus: IStatus | null;
    activityId: string;
    parentName: string;
    grandParentName: string;
    plannedDuration: number;
    actualDuration: number;
    remainingDuration: number;
    completedDuration: number;
    activityType: string;
    activityName: string;
    overrunDays: number;
    overrunPercent: number;
    percentileRank: number;
    criticalPath: boolean;
    delayDriver: boolean;
    currentStartDate: number;
    currentFinishDate: number;
    baselineStartDate: number;
    baselineFinishDate: number;
    forecastDuration: number | null;
    activityStatus: string | null;
    lateStart: number;
    lateFinish: number;
    durationOverun: number;
    forecastOverrun: number | null;
    versionName: string | null;
    taskId: number | null;
    projectName: string | null;
    contractName: string | null;
    clusterId: string | null;
    cardBoardViewResponse: ICardBoardViewResponse;
}

export interface IProgramProgressInput {
    taskCode: string;
    description: string;
    level: number;
    actualStartDate: number | null;
    actualEndDate: number | null;
    plannedStartDate: number;
    plannedEndDate: number;
    color: string;
    'duration%Complete': number;
}

export interface IProgramProgressOutput {
    actualEndDate: string;
    actualStartDate: string;
    color: string;
    durationComplete: number;
    end: Date;
    level: number;
    plannedEndDate: string;
    plannedStartDate: string;
    start: Date;
    taskCode: string;
    title: string;
    y: number;
}

export const getDecodedValue = (
    value: ProgressMeasurement,
): { progressMeasurement: PrefferedSPIMethodResponse; partialCompletionCredit: PreferencePartialCompletion } => {
    switch (value) {
        case 'activityCompleted': {
            return { progressMeasurement: 'ACTIVITIES_COMPLETE_S_CURVE', partialCompletionCredit: 'false' };
        }
        case 'activityCompletedCriticality': {
            return { progressMeasurement: 'CRITICALITY_WEIGHT_S_CURVE', partialCompletionCredit: 'false' };
        }
        case 'activitySubmittedCompletion': {
            return { progressMeasurement: 'ACTIVITIES_COMPLETE_S_CURVE', partialCompletionCredit: 'true' };
        }
        case 'activitySubmittedCompletionCriticality': {
            return { progressMeasurement: 'CRITICALITY_WEIGHT_S_CURVE', partialCompletionCredit: 'true' };
        }
        case 'resourceUnits': {
            return { progressMeasurement: 'RESOURCE_UNITS_S_CURVE', partialCompletionCredit: 'true' };
        }
    }
};

export const getEncodeValue = (
    progressMeasurementDefaultValue: PrefferedSPIMethodResponse,
    partialCompletionCreditDefaultValue: PreferencePartialCompletion,
): ProgressMeasurement | undefined => {
    if (
        progressMeasurementDefaultValue === 'ACTIVITIES_COMPLETE_S_CURVE' &&
        partialCompletionCreditDefaultValue === 'false'
    ) {
        return 'activityCompleted';
    }
    if (
        progressMeasurementDefaultValue === 'CRITICALITY_WEIGHT_S_CURVE' &&
        partialCompletionCreditDefaultValue === 'false'
    ) {
        return 'activityCompletedCriticality';
    }
    if (
        progressMeasurementDefaultValue === 'ACTIVITIES_COMPLETE_S_CURVE' &&
        partialCompletionCreditDefaultValue === 'true'
    ) {
        return 'activitySubmittedCompletion';
    }
    if (
        progressMeasurementDefaultValue === 'CRITICALITY_WEIGHT_S_CURVE' &&
        partialCompletionCreditDefaultValue === 'true'
    ) {
        return 'activitySubmittedCompletionCriticality';
    }
    if (progressMeasurementDefaultValue === 'RESOURCE_UNITS_S_CURVE') {
        return 'resourceUnits';
    }
    return undefined;
};
